import useSWR from 'swr'
import { Info } from '@digital-twins/design-system/icons'
import { Text } from '@design-system/components/Text'
import { cn } from '@/lib/utils'
import { CreditIcon } from '@/components/icons/CreditIcon'
import type { CreditInfo, SubscriptionTier } from '@/lib/api'
import { usersApi } from '@/lib/api'
import { isTrialLike, isFairgenInternalTier } from '@/lib/billing/tier'
import { planTierDisplayName } from './displayNames'
import { formatUsd } from './formatMoney'
import { OverageInfoTooltip } from './OverageInfoTooltip'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from 'react-i18next'
import { intlLocaleForUi } from '@/i18n'

interface PlanSummaryCardProps {
  tier: SubscriptionTier
  creditInfo: CreditInfo | null | undefined
  /** When true, balance/creditLimit reflect the org-level shared pool (Enterprise). */
  isPoolBilling?: boolean
  daysUntilReset: number
  renewsAt?: string | null
  trialEndsAt?: string | null
  endsAt?: string | null
  lsStatus?: string | null
  /**
   * Per-credit overage rate in cents (the rate the cap is enforced at, not the credit display
   * value). Zero hides the dollar figures.
   */
  overageRateCents: number
}

const DAY_MS = 1000 * 60 * 60 * 24

type StatusKey = 'expired' | 'cancelled' | 'pastDue' | 'paused' | 'trial' | 'noPlan'

function lsStatusKey(lsStatus: string | null | undefined): StatusKey | null {
  switch (lsStatus) {
    case 'expired':
      return 'expired'
    case 'cancelled':
      return 'cancelled'
    case 'past_due':
      return 'pastDue'
    case 'paused':
      return 'paused'
    default:
      return null
  }
}

function validDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function PlanSummaryCard(props: PlanSummaryCardProps) {
  const {
    tier,
    creditInfo,
    isPoolBilling = false,
    daysUntilReset,
    trialEndsAt,
    endsAt,
    lsStatus,
    overageRateCents,
  } = props
  const { t, i18n } = useTranslation('app')
  const numberLocale = intlLocaleForUi(i18n.language)

  const { data: bonusData } = useSWR('me/credits/bonus', () => usersApi.getBonusCredits(), {
    revalidateOnFocus: false,
  })
  const bonusBalance = bonusData?.balance ?? 0
  const isInternal = isFairgenInternalTier(tier)
  const used = creditInfo?.used ?? 0
  const limit = creditInfo?.creditLimit ?? 0
  const overageUsed = creditInfo?.overageUsed ?? 0
  // Source of truth: backend already encodes "did this user exceed their plan?".
  const isOver = !isInternal && (creditInfo?.isInOverage ?? false)

  const pct = isInternal ? 0 : limit > 0 ? Math.round((used / limit) * 100) : 0
  // Visual width caps at 100%; overflow is communicated by the dot at the end of
  // the track and the "over plan" text.
  const fillPct = Math.min(pct, 100)
  const balance = Math.max(0, limit - used)

  const formatDate = (date: Date) => {
    const sameYear = date.getFullYear() === new Date().getFullYear()
    return new Intl.DateTimeFormat(numberLocale, {
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    }).format(date)
  }

  // What the client is on: no plan, a trial, or a subscription state worth naming.
  const isNoPlan = tier === 'locked_free'
  const isTrial = isTrialLike(tier)
  const statusKey: StatusKey | null = isNoPlan ? 'noPlan' : isTrial ? 'trial' : lsStatusKey(lsStatus)
  const statusText = statusKey ? t(`billing.status.${statusKey}`) : null

  // Credits always refresh monthly, whatever the billing interval. `daysUntilReset` is the
  // source of truth (billing_cycle_anchor + cycleDays); shown as the date it lands on.
  // Trials show their end date, cancelled subscriptions the date access ends.
  const dateLine = (() => {
    if (isNoPlan) return null
    if (isTrial) {
      const trialDate = validDate(trialEndsAt)
      if (trialDate) {
        return trialDate.getTime() > Date.now()
          ? t('billing.myPlan.trialEnds', { date: formatDate(trialDate) })
          : t('billing.myPlan.trialEnded', { date: formatDate(trialDate) })
      }
    }
    if (lsStatus === 'cancelled') {
      const endsDate = validDate(endsAt)
      if (endsDate) return t('billing.myPlan.accessEnds', { date: formatDate(endsDate) })
    }
    const resetDate = new Date(Date.now() + Math.max(0, daysUntilReset) * DAY_MS)
    return t('billing.myPlan.resetsOn', { date: formatDate(resetDate) })
  })()

  const planName = planTierDisplayName(isInternal ? tier : 'enterprise')
  const overageDollars = (overageUsed * overageRateCents) / 100

  return (
    <div data-testid="billing-my-plan" className="flex flex-col rounded-xl bg-muted">
      <div data-testid="billing-plan-summary" className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
          <div className="flex min-w-0 flex-col gap-1">
            <Text size="sm" color="muted">
              {t('billing.myPlan.title')}
            </Text>
            <div className="flex flex-wrap items-center gap-2">
              {isNoPlan || isTrial ? (
                <span
                  data-testid="billing-my-plan-status"
                  className="font-display text-2xl leading-none text-foreground"
                >
                  {statusText}
                </span>
              ) : (
                <>
                  <span className="font-display text-2xl leading-none text-foreground">{planName}</span>
                  {statusText ? (
                    <span
                      data-testid="billing-my-plan-status"
                      className={cn(
                        'w-fit rounded-full px-2.5 py-1 text-xs font-medium leading-none',
                        statusKey === 'expired' && 'bg-destructive/15 text-destructive',
                        statusKey === 'cancelled' && 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
                        statusKey === 'pastDue' && 'bg-destructive/15 text-destructive',
                        statusKey === 'paused' && 'bg-foreground/10 text-muted-foreground',
                      )}
                    >
                      {statusText}
                    </span>
                  ) : null}
                </>
              )}
            </div>
          </div>
          {!isOver && !isInternal ? (
            <span
              data-testid="billing-plan-summary-pct"
              className="rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium leading-none text-foreground"
            >
              {t('billing.pctUsed', { pct })}
            </span>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          <Text size="sm" color="muted" className="flex items-center gap-1">
            <CreditIcon className="h-3.5 w-3.5" />
            {isPoolBilling ? t('billing.teamCreditPool') : t('billing.creditsThisCycle')}
          </Text>
          {isInternal ? (
            <div className="flex items-end justify-end">
              <span className="font-display text-3xl leading-none text-foreground">
                {t('billing.unlimited')}
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-end justify-between">
                <div className="flex items-center gap-1.5">
                  {!isOver ? (
                    <Text size="xs" color="muted" className="dark:text-white/60">
                      {t('billing.remaining', { count: balance.toLocaleString(numberLocale) })}
                    </Text>
                  ) : null}
                  {!isOver && bonusBalance > 0 && (
                    <>
                      <Text size="xs" color="muted" className="dark:text-white/60">·</Text>
                      <Text size="xs" className="text-amber-600 dark:text-amber-400 font-medium">
                        {t('billing.bonus', { count: bonusBalance.toLocaleString(numberLocale) })}
                      </Text>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 cursor-default text-muted-foreground hover:text-foreground transition-colors" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-center">
                          {t('billing.bonusTooltip')}
                        </TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-3xl leading-none tabular-nums text-foreground">
                    {used.toLocaleString(numberLocale)}
                  </span>
                  <span className="font-display text-sm leading-none text-muted-foreground">
                    {t('billing.ofLimit', { count: limit.toLocaleString(numberLocale) })}
                  </span>
                </div>
              </div>

              <div className="relative h-2 w-full overflow-hidden rounded-full bg-foreground/10">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-500"
                  style={{ width: `${fillPct}%` }}
                />
                {isOver ? (
                  <span
                    aria-hidden
                    className="absolute right-0 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-primary ring-2 ring-card"
                  />
                ) : null}
              </div>
            </>
          )}

          {dateLine || isOver ? (
            <div className="flex flex-col gap-y-0.5 text-sm text-muted-foreground">
              {dateLine ? <span data-testid="billing-my-plan-date">{dateLine}</span> : null}
              {isOver ? (
                <span data-testid="billing-my-plan-over">
                  {t('billing.myPlan.overPlan', { count: overageUsed, formatted: overageUsed.toLocaleString(numberLocale) })}
                  {overageRateCents > 0 ? <span> ({formatUsd(overageDollars)}) </span> : ' '}
                  <OverageInfoTooltip
                    overageCredits={overageUsed}
                    rateCents={overageRateCents}
                    side="top"
                    triggerTestId="billing-plan-summary-overage-info"
                  />
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
